import _ from 'lodash';
import {
  MedicalResult,
  MedicalTranscriptEvent,
  StartMedicalStreamTranscriptionCommand,
  StartMedicalStreamTranscriptionCommandInput,
  TranscribeStreamingClient,
} from '@aws-sdk/client-transcribe-streaming';
import { AudioService, DualMediaStreamAudioService, WindowEventAudioService } from './MicService';
import { AWSTranscribeResultBuilder } from './AWSTranscribeResultBuilder';
import constants from './constants';
import { v4 as uuidV4 } from 'uuid';
import {
  AWSCredentials,
  AppointmentTranscription,
  AppointmentTranscriptionMessage,
  AppointmentTranscriptionMessageFromEnum,
} from 'generated/api';

import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { store } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';

export const TranscriptionType = {
  conversation: 'CONVERSATION',
  dictation: 'DICTATION',
} as const;

export type TranscriptionCallback = (value: string, event?: MedicalTranscriptEvent) => void;

export type AppointmentTranscriptionWithPartialIndicators = AppointmentTranscription & {
  messages: (AppointmentTranscriptionMessage & { isPartial?: boolean })[];
};

export function AppointmentTranscriptionFromAWS(
  appointment_id: string,
  results: MedicalResult[],
  withPartials?: boolean,
  withPartialIndicators?: true,
): AppointmentTranscriptionWithPartialIndicators;
export function AppointmentTranscriptionFromAWS(
  appointment_id: string,
  results: MedicalResult[],
  withPartials?: boolean,
  withPartialIndicators?: false,
): AppointmentTranscription;
export function AppointmentTranscriptionFromAWS(
  appointment_id: string,
  results: MedicalResult[],
  withPartials?: boolean,
  withPartialIndicators?: boolean,
): AppointmentTranscription {
  const messages = _(results)
    .groupBy('ResultId')
    .mapValues(_.last) //we only want the last because the ones before are partials
    .values()
    .filter((it) => !!it && (withPartials ? true : !it.IsPartial))
    .sortBy('StartTime')
    .map((result: MedicalResult) => {
      const text = result.Alternatives?.[0]!.Transcript;
      const from =
        result.ChannelId === 'ch_0'
          ? AppointmentTranscriptionMessageFromEnum.Staff
          : AppointmentTranscriptionMessageFromEnum.Patient;
      const date = undefined; //TODO
      if (!text || !from) {
        return undefined;
      }
      const res = { from, date, text };
      if (withPartials && withPartialIndicators) {
        (res as any).isPartial = result.IsPartial;
      }
      return res satisfies AppointmentTranscriptionMessage;
    })
    .filter()
    .value() as unknown as AppointmentTranscriptionMessage[];

  return {
    id: appointment_id,
    messages,
  } satisfies AppointmentTranscription;
}

export function AppointmentTranscriptionFromAWSWithVoicePartition(
  appointment_id: string,
  results: MedicalResult[],
): AppointmentTranscription {
  const items = _(results)
    .groupBy('ResultId')
    .mapValues(_.last) //we only want the last because the ones before are partials
    .values()
    .compact()
    .filter((it) => !it.IsPartial)
    .sortBy('StartTime')
    .flatMap((it) => it.Alternatives?.[0]?.Items)
    .compact()
    .value();

  const messages = [];
  let lastSpeaker = null;
  for (let item of items) {
    const createNewMessage =
      lastSpeaker === null || (!!item.Speaker && item.Speaker !== lastSpeaker);
    if (createNewMessage) {
      messages.push({
        from: AppointmentTranscriptionMessageFromEnum[item?.Speaker === '0' ? 'Staff' : 'Patient'],
        text: item.Content ?? '',
      });
    } else {
      const message = _.last(messages);
      if (message) {
        message.text += ' ' + (item.Content ?? '');
      }
    }
    if (item.Speaker) {
      lastSpeaker = item.Speaker;
    }
  }

  return {
    id: appointment_id,
    messages,
  } satisfies AppointmentTranscription;
}

export class AwsTranscribeService {
  private client: TranscribeStreamingClient;
  private ongoingSessions = new Set();
  private currentSession?: string;
  private resultBuilder = new AWSTranscribeResultBuilder();
  private isStopping = false;
  protected onError?: (err: unknown) => void;
  private isReconnecting = false;
  private shouldBeRunning = false;
  private reconnectTimeouts: NodeJS.Timeout[] = [];
  private maxReconnectAttempts = 5;
  private reconnectAttempt = 0;
  private lastSettings?: StartMedicalStreamTranscriptionCommandInput;
  private streamEndedUnexpectedly = false;

  constructor(
    private credentials: AWSCredentials,
    protected audioProvider: AudioService,
    public transcriptionCallback?: TranscriptionCallback,
    errorCallback?: (err: unknown) => void,
    private overrideSettings?: Partial<StartMedicalStreamTranscriptionCommandInput>,
  ) {
    this.client = new TranscribeStreamingClient({
      region: 'ca-central-1',
      credentials: {
        accessKeyId: credentials.key_id,
        secretAccessKey: credentials.secret,
      },
    });
    this.onError = errorCallback;
    this.setupNetworkListeners();
  }

  isVoicePartitioned() {
    return !!this.overrideSettings?.ShowSpeakerLabel;
  }

  async start() {
    this.shouldBeRunning = true;
    this.reconnectAttempt = 0;
    // Stale from a previous session's stream drop; left set it makes
    // isNetworkError() classify EVERY later error (e.g. mic permission
    // revoked) as network-related, defeating the fatal-start rejection.
    this.streamEndedUnexpectedly = false;
    this.clearReconnectTimeouts();
    return this.internalStart();
  }

  private async internalStart(): Promise<void> {
    if (!this.shouldBeRunning) {
      return;
    }

    try {
      this.currentSession = this.startNewSession();
      if (this.reconnectAttempt === 0) {
        this.resultBuilder.reset();
      }

      const newSettings: StartMedicalStreamTranscriptionCommandInput = {
        LanguageCode: 'en-US',
        MediaSampleRateHertz: constants.AUDIO_RATE,
        MediaEncoding: 'pcm',
        Specialty: 'PRIMARYCARE',
        Type: TranscriptionType.conversation,
        SessionId: this.currentSession,
        // ContentIdentificationType: "PHI",
        EnableChannelIdentification: true,
        NumberOfChannels: 2,
        VocabularyName: 'CareAnd',
        ...(this.overrideSettings ?? {}),
        AudioStream: await this.audioProvider.startRecording(),
      };

      this.lastSettings = newSettings;
      const command = new StartMedicalStreamTranscriptionCommand(newSettings);

      const res = await this.client.send(command);

      if (!res) {
        throw new Error('Failed to start transcription');
      }
      if (!res.TranscriptResultStream) {
        throw new Error('Failed to get TranscriptionResultStream');
      }

      // Reset reconnect attempt on successful connection
      this.reconnectAttempt = 0;
      this.isReconnecting = false;

      const runAsync = async () => {
        try {
          console.log('[AWS Transcribe] Stream started, waiting for results...');
          let hasReceivedAnyData = false;

          for await (let result of res.TranscriptResultStream!) {
            console.log(
              '[AWS Transcribe] Received stream result:',
              result.TranscriptEvent?.Transcript?.Results?.length || 0,
              'results',
            );

            if (!this.shouldBeRunning) {
              console.log('[AWS Transcribe] Stream stopping due to shouldBeRunning=false');
              break;
            }

            hasReceivedAnyData = true;
            this.resultBuilder.accept(result);
            const currentValue = this.resultBuilder.getCurrentTextValue();
            this.transcriptionCallback?.(currentValue, result.TranscriptEvent);
          }

          // Stream ended - check if this was expected
          console.log(
            '[AWS Transcribe] Stream ended. HasReceivedData:',
            hasReceivedAnyData,
            'ShouldBeRunning:',
            this.shouldBeRunning,
          );

          if (this.shouldBeRunning && !this.isStopping) {
            console.log('[AWS Transcribe] Stream ended unexpectedly, attempting reconnection...');
            this.streamEndedUnexpectedly = true;
            this.handleStreamError(new Error('Stream ended unexpectedly'));
          }
        } catch (err: unknown) {
          // Filter out expected WebSocket closure errors during reconnection
          const isWebSocketClosure = (err as any)?.message?.includes(
            'WebSocket is already in CLOSING or CLOSED state',
          );

          if (!isWebSocketClosure) {
            console.error('[AWS Transcribe] Stream error:', err);
          } else {
            console.log('[AWS Transcribe] WebSocket cleanup (expected during reconnection)');
          }

          if (this.shouldBeRunning && !this.isStopping && !isWebSocketClosure) {
            this.handleStreamError(err);
          }
        }
      };

      runAsync();
    } catch (err: any) {
      // Filter out expected WebSocket closure errors during reconnection
      const isWebSocketClosure = err?.message?.includes(
        'WebSocket is already in CLOSING or CLOSED state',
      );

      if (!isWebSocketClosure) {
        console.error('[AWS Transcribe] Start error:', err);
      } else {
        console.log(
          '[AWS Transcribe] WebSocket cleanup during start (expected during reconnection)',
        );
      }

      if (this.isReconnecting && !isWebSocketClosure) {
        // Called from attemptReconnect: let its catch schedule the next retry
        // — swallowing here would log a bogus 'Successfully reconnected!' and
        // wedge the retry loop with isReconnecting stuck true.
        throw err;
      }

      const isInitialStart = this.reconnectAttempt === 0 && !this.isReconnecting;
      if (isInitialStart && !isWebSocketClosure && !this.isNetworkError(err)) {
        // Fatal at start time (mic permission denied, bad credentials, …):
        // reject start() so callers show their error state — resolving here
        // would let them assert a live recording on a dead provider.
        await this.forceStop();
        throw err;
      }

      if (this.shouldBeRunning && !this.isStopping && !isWebSocketClosure) {
        this.handleStreamError(err);
      }
    }
  }

  private isNetworkError(error: unknown): boolean {
    if (!error) return false;

    console.log('[AWS Transcribe] Checking error type:', error);

    const errorString = error.toString().toLowerCase();
    const errorMessage = (error as any)?.message?.toLowerCase() || '';
    const errorName = (error as any)?.name?.toLowerCase() || '';
    const errorCode = (error as any)?.code?.toLowerCase() || '';

    // Check if this is our custom stream ended error
    const isStreamIssue =
      errorMessage.includes('stream ended unexpectedly') || this.streamEndedUnexpectedly;

    const isNetworkRelated =
      errorName.includes('network') ||
      errorCode.includes('network') ||
      errorMessage.includes('network') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('socket') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('unreachable') ||
      errorMessage.includes('disconnected') ||
      errorString.includes('networkerror') ||
      errorString.includes('connectionerror') ||
      (error as any)?.name === 'NetworkError' ||
      (error as any)?.code === 'NetworkError';

    const shouldReconnect = isStreamIssue || isNetworkRelated;
    console.log(
      '[AWS Transcribe] Error analysis - isStreamIssue:',
      isStreamIssue,
      'isNetworkRelated:',
      isNetworkRelated,
      'shouldReconnect:',
      shouldReconnect,
    );

    return shouldReconnect;
  }

  private handleStreamError(error: unknown): void {
    if (!this.shouldBeRunning || this.isStopping || this.isReconnecting) {
      return;
    }

    console.log('[AWS Transcribe] Handling stream error, checking if network-related...');

    if (this.isNetworkError(error)) {
      console.log('[AWS Transcribe] Network error detected, attempting reconnection...');
      this.attemptReconnect();
    } else {
      console.log('[AWS Transcribe] Non-network error, passing to error handler');
      this.onError?.(error);
      this.forceStop();
    }
  }

  private attemptReconnect(): void {
    if (
      this.isReconnecting ||
      !this.shouldBeRunning ||
      this.reconnectAttempt >= this.maxReconnectAttempts
    ) {
      if (this.reconnectAttempt >= this.maxReconnectAttempts) {
        console.error('[AWS Transcribe] Max reconnection attempts reached, giving up');
        this.onError?.(new Error('Max reconnection attempts reached'));
        this.forceStop();
      }
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempt += 1;

    // Exponential backoff with jitter: 1s, 2s, 4s, 8s, 16s
    const baseDelay = 2 ** (this.reconnectAttempt - 1) * 1000;
    const jitter = Math.random() * 1000; // Add up to 1s jitter
    const delay = Math.min(baseDelay + jitter, 30000); // Cap at 30s

    console.log(
      `[AWS Transcribe] Reconnection attempt ${this.reconnectAttempt}/${
        this.maxReconnectAttempts
      } in ${Math.round(delay)}ms`,
    );

    const timeout = setTimeout(async () => {
      if (!this.shouldBeRunning) {
        this.isReconnecting = false;
        return;
      }

      try {
        // Check if we're online before attempting reconnection
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          console.log('[AWS Transcribe] Still offline, will retry when online');
          this.isReconnecting = false;
          this.waitForOnline();
          return;
        }

        console.log('[AWS Transcribe] Attempting to restart transcription...');

        // Clean up current client and create fresh one
        if (this.client) {
          try {
            this.client.destroy();
          } catch (err: any) {
            console.log('[AWS Transcribe] Error destroying old client:', err);
          }
          // Wait a moment for the old client to fully cleanup
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        this.client = new TranscribeStreamingClient({
          region: 'ca-central-1',
          credentials: {
            accessKeyId: this.credentials.key_id,
            secretAccessKey: this.credentials.secret,
          },
        });

        // Also clean up and restart audio provider
        try {
          await this.audioProvider.stop();
          // Small delay to let audio cleanup
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (err: any) {
          console.log('[AWS Transcribe] Error stopping audio provider:', err);
        }

        await this.internalStart();
        console.log('[AWS Transcribe] Successfully reconnected!');
      } catch (err: any) {
        console.error(
          `[AWS Transcribe] Reconnection attempt ${this.reconnectAttempt} failed:`,
          err,
        );
        this.isReconnecting = false;
        this.attemptReconnect(); // Try again
      }
    }, delay);

    this.reconnectTimeouts.push(timeout);
  }

  private waitForOnline(): void {
    if (typeof window === 'undefined') {
      // Not in browser environment, just retry
      this.isReconnecting = false;
      this.attemptReconnect();
      return;
    }

    const handleOnline = () => {
      console.log('[AWS Transcribe] Network restored, attempting reconnection...');
      window.removeEventListener('online', handleOnline);
      this.isReconnecting = false;
      this.attemptReconnect();
    };

    window.addEventListener('online', handleOnline);
  }

  private clearReconnectTimeouts(): void {
    this.reconnectTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.reconnectTimeouts = [];
  }

  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') {
      return; // Not in browser environment
    }

    const handleOffline = () => {
      console.log('[AWS Transcribe] Network went offline');
      // Don't immediately stop, but mark for reconnection when back online
    };

    const handleOnline = () => {
      console.log('[AWS Transcribe] Network came back online');

      // If we should be running but aren't transcribing, try to reconnect
      if (this.shouldBeRunning && !this.isStopping && !this.isReconnecting) {
        console.log('[AWS Transcribe] Attempting to reconnect after network restored...');
        setTimeout(() => {
          this.handleStreamError(new Error('Network restored - reconnecting'));
        }, 2000); // Give network a moment to stabilize
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Store references to remove them later if needed
    (this as any)._networkHandlers = { handleOffline, handleOnline };
  }

  private removeNetworkListeners(): void {
    if (typeof window === 'undefined' || !(this as any)._networkHandlers) {
      return;
    }

    const { handleOffline, handleOnline } = (this as any)._networkHandlers;
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    delete (this as any)._networkHandlers;
  }

  async stop() {
    this.shouldBeRunning = false;
    this.clearReconnectTimeouts();
    this.removeNetworkListeners();

    if (this.isStopping) {
      console.log('[aws] already stopping');
      return;
    }
    this.isStopping = true;
    // Promote any pending partial to a completion so we don't lose the last words
    // and so isFinal() returns true (the stream is already closed at this point)
    this.resultBuilder.finalizePartial();
    let didSetNoContentTimeout = false;
    let sessionId = this.currentSession;
    await new Promise<void>((fulfill, reject) => {
      // Maximum timeout to prevent hanging indefinitely
      const maxTimeout = setTimeout(() => {
        clearInterval(id);
        this.forceStop();
        reject(new Error('Transcription timeout after 30 seconds'));
      }, 30_000);

      const id = setInterval(() => {
        // Check if session has changed - exit early if so
        if (this.currentSession !== sessionId) {
          clearInterval(id);
          clearTimeout(maxTimeout);
          fulfill();
          return;
        }

        const hasFinished = this.resultBuilder.isFinal() && this.resultBuilder.hasAnyCompletion();
        const hasNoTranscriptionYet = () =>
          !this.resultBuilder.hasPartial() && !this.resultBuilder.hasAnyCompletion();

        if (hasFinished) {
          clearInterval(id);
          clearTimeout(maxTimeout);
          this.forceStop();
          fulfill();
          return;
        }

        if (!didSetNoContentTimeout && hasNoTranscriptionYet()) {
          didSetNoContentTimeout = true;
          setTimeout(() => {
            if (this.currentSession !== sessionId) {
              fulfill();
              return;
            }
            if (hasNoTranscriptionYet()) {
              clearInterval(id);
              clearTimeout(maxTimeout);
              this.forceStop();
              fulfill();
            } else {
              didSetNoContentTimeout = false;
            }
          }, 2_000);
        }
      }, 200);
    });
  }

  private async forceStop() {
    try {
      console.log('[aws] forceStop');
      this.shouldBeRunning = false;
      this.streamEndedUnexpectedly = false;
      this.clearReconnectTimeouts();
      await this.audioProvider.stop();
      this.endSession();
      this.isStopping = false;
      this.isReconnecting = false;
      this.client?.destroy();
    } catch (e: unknown) {
      console.log(e);
      this.onError?.(e);
    }
  }

  private endSession(session?: string) {
    session = session || this.currentSession;

    this.ongoingSessions.delete(session);
    if (this.currentSession === session) {
      this.currentSession = undefined;
    }
  }

  private startNewSession() {
    const uuid = uuidV4();
    this.ongoingSessions.add(uuid);
    return uuid;
  }

  static async getCredentials() {
    return getUnwrappedDispatch(store.dispatch)(api.AI.getAWSTranscribeCredentials());
  }
}

export class JitsiIFrameTranscriptionService extends AwsTranscribeService {
  private handshakeListener?: (ev: MessageEvent<any>) => void;

  constructor(
    credentials: AWSCredentials,
    transcriptionCallback?: TranscriptionCallback,
    audioProvider: AudioService = new WindowEventAudioService(),
    errorCallback?: (err: unknown) => void,
  ) {
    super(credentials, audioProvider, transcriptionCallback, errorCallback);
  }

  override async start(_iframe?: HTMLIFrameElement) {
    this.clearHandshakeListener();

    // Start transcription service when iframe anounces it's ready
    const onAudioReady = (ev: MessageEvent<any>) => {
      if (ev.origin !== 'https://video2.careand.ca') {
        return;
      }
      if (ev.data?.type !== 'audio-ready') {
        return;
      }
      this.clearHandshakeListener();
      this.sendAudioReady(_iframe);
      super.start().catch((err: unknown) => {
        console.error('[AWS Transcribe] Failed to start after audio-ready:', err);
        this.onError?.(err);
      });
    };
    this.handshakeListener = onAudioReady;
    window.addEventListener('message', onAudioReady);

    this.sendAudioReady(_iframe);
  }

  override async stop() {
    // A late or replayed 'audio-ready' must not restart a stopped stream.
    this.clearHandshakeListener();
    return super.stop();
  }

  private clearHandshakeListener() {
    if (this.handshakeListener) {
      window.removeEventListener('message', this.handshakeListener);
      this.handshakeListener = undefined;
    }
  }

  private sendAudioReady(iframe?: HTMLIFrameElement) {
    // Announce to Jitsi iframe that we're ready to receive audio
    const targetIframe =
      iframe ?? (document.querySelector("iframe[id*='jitsiConference']") as HTMLIFrameElement);
    targetIframe?.contentWindow?.postMessage(
      {
        type: 'audio-receiver-ready',
        origin: window.origin,
      },
      '*',
    );
  }
}

export class DualMediaStreamTranscriptionService extends AwsTranscribeService {
  public onStreamsUpdated: DualMediaStreamAudioService['onStreamsUpdated'];
  public setMuted: DualMediaStreamAudioService['setMuted'];

  constructor(credentials: AWSCredentials, transcriptionCallback?: TranscriptionCallback) {
    const audioProvider = new DualMediaStreamAudioService();
    super(credentials, audioProvider, transcriptionCallback);
    this.onStreamsUpdated = audioProvider.onStreamsUpdated;
    this.setMuted = audioProvider.setMuted;
  }

  override async start(_iframe?: HTMLIFrameElement) {
    const isReady = await (this.audioProvider as DualMediaStreamAudioService).isReady();
    if (!isReady) {
      throw new Error('failed to start AWSTranscribeService: NOT READY');
    }
    await super.start();
  }
}
